/**
 * ScoreDot — the circular score chip used for boosters and orbital nodes.
 *
 * Body shows the score (e.g. "+47%"), outlined with the band colour. NA renders
 * a softer purple-grey outline and "N/A" text. Optional onClick turns the dot
 * into a button (used by the orbital model + hero contrast meters).
 */

import React from 'react';
import { BAND_PALETTE, formatBipolarScore } from '../config/bandColours';
import type { Band } from '../types';

export interface ScoreDotProps {
  value: number | null;
  band: Band;
  /** Pixel diameter. */
  size?: number;
  /** Dim to ~40% opacity (used by orbital to gray out inactive drivers). */
  dim?: boolean;
  onClick?: () => void;
}

const NAVY = '#0F172A';

export const ScoreDot: React.FunctionComponent<ScoreDotProps> = ({
  value,
  band,
  size = 56,
  dim = false,
  onClick,
}) => {
  const palette = BAND_PALETTE[band];
  const isNA = value == null;
  const display = isNA ? 'N/A' : formatBipolarScore(value);
  const sharedStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: isNA ? '#B9ADDA' : NAVY,
    color: '#fff',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: Math.round(size * 0.3),
    letterSpacing: '-0.02em',
    boxShadow: '0 6px 16px rgba(20,30,55,0.18)',
    opacity: dim ? 0.4 : 1,
    flex: '0 0 auto',
    outline: `3px solid ${palette.line}`,
    outlineOffset: 2,
  };
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...sharedStyle, cursor: 'pointer' }}
      >
        {display}
      </button>
    );
  }
  return <div style={sharedStyle}>{display}</div>;
};
